// How old is the athlete's last go at ONE exercise, and is that old enough
// to say so (LG-49). Pure: takes epoch ms, returns a verdict plus the raw
// day counts. The wording ("6 sett.") and the colour live in the UI — this
// module only decides WHICH of the four cases holds, so the thresholds are
// jest-locked in one place instead of being re-guessed per surface.
//
// ⭐ The two thresholds are MEASURED, not invented (Antenore's 22 330 sets,
// three athletes, three years — [[feedback_derive_thresholds_from_data_not_constants]]):
//   • 28 days without an exercise the athlete otherwise trains fires on
//     12.8% of resumptions (Erika 16.7%, Leonardo 6.9%) — about one in
//     eight, which is rare enough to still mean something. At 14 days it
//     would be one in five and the signal would switch itself off.
//   • A layoff longer than 14 days happened 4 times in 1 210 intervals.
//     It is the rarer, louder case, so it is the one that earns `danger`.

import { daysSince } from "./freshness.js";

/** Days without THIS exercise before its age is worth stating. */
export const STALE_EXERCISE_DAYS = 28;
/** Days without training AT ALL that turn a stale exercise into a layoff. */
export const LAYOFF_DAYS = 14;

export type StalenessLevel =
  /** Never logged — there is no age to state; the panel offers an estimate. */
  | "never"
  /** Recent enough that saying anything would be noise. */
  | "fresh"
  /** Still training, but not this — the common case, and the useful one. */
  | "warn"
  /** Away from the gym AND away from this lift. Rare by construction. */
  | "danger";

export type Staleness = {
  level: StalenessLevel;
  /** Whole days since this exercise was last logged; null when never. */
  daysSinceExercise: number | null;
  /** Whole days since the athlete trained at all; null when never. */
  daysSinceTraining: number | null;
};

export function exerciseStaleness(input: {
  /** Last set logged for this exercise, epoch ms. Null = never done it. */
  lastExerciseAtMs: number | null;
  /** Last session of ANY kind, epoch ms. Null = never trained. */
  lastSessionAtMs: number | null;
  nowMs: number;
}): Staleness {
  const { lastExerciseAtMs, lastSessionAtMs, nowMs } = input;
  const daysSinceExercise = daysSince(lastExerciseAtMs, nowMs);
  const daysSinceTraining = daysSince(lastSessionAtMs, nowMs);

  if (daysSinceExercise === null)
    return { level: "never", daysSinceExercise, daysSinceTraining };

  // `danger` needs BOTH facts, and the layoff one can be unknown (a fresh
  // install reading a single exercise). Unknown is not a layoff: an absent
  // answer never becomes an affirmative one
  // ([[feedback_empty_vs_unresolved_collection]]), so it falls through to
  // the `warn` test rather than escalating on a guess.
  if (
    daysSinceExercise >= STALE_EXERCISE_DAYS &&
    daysSinceTraining !== null &&
    daysSinceTraining > LAYOFF_DAYS
  )
    return { level: "danger", daysSinceExercise, daysSinceTraining };

  if (daysSinceExercise >= STALE_EXERCISE_DAYS)
    return { level: "warn", daysSinceExercise, daysSinceTraining };

  return { level: "fresh", daysSinceExercise, daysSinceTraining };
}
